const { differenceInCalendarMonths, subYears } = require('date-fns')
const {
  checkForStockReportRateRequirements,
  buildStockReportRateQueryContext,
  buildStockReportRateExportQueryContext,
  buildStockReportRateSummaryQuery,
  buildStockReportRateLocationTableQuery,
  buildStockReportRateCycleByCycleQuery,
  buildStockReportRateProgramCyclesQuery,
  buildStockReportRateExportRowsQuery,
  getStockReportRateLevel,
  getIncompletePeriodsStartDate
} = require('./operational/stock-report-rate')
const {
  getRevenueSummary: getRevenueSummaryData,
  getRevenueByLocationTable: getRevenueByLocationTableData,
  getRevenueByProductTable: getRevenueByProductTableData,
  getRevenueMissingPricesSummary: getRevenueMissingPricesSummaryData,
  getMonthlyRevenue: getMonthlyRevenueData,
  parseDateOnlyUtcOrThrow
} = require('./financial/revenue')
const {
  getGrossProfitSummary: getGrossProfitSummaryData,
  getGrossProfitByProductTable: getGrossProfitByProductTableData,
  getGrossProfitByLocationTable: getGrossProfitByLocationTableData,
  getGrossProfitMissingPricesSummary: getGrossProfitMissingPricesSummaryData,
  getMonthlyGrossMargin: getMonthlyGrossMarginData
} = require('./financial/gross-profit')
const {
  getLossesSummary: getLossesSummaryData,
  getLossesByProductTable: getLossesByProductTableData,
  getLossesByLocationTable: getLossesByLocationTableData,
  getLossesMissingPricesSummary: getLossesMissingPricesSummaryData,
  getMonthlyLosses: getMonthlyLossesData
} = require('./financial/losses')
const { resolveServiceIds } = require('./utils')

function normalizeProgramCycleFromOptions (options) {
  if (!options) {
    return null
  }

  const periods = options.report && Array.isArray(options.report.periods) ? options.report.periods : []
  const periodType = periods[0] && periods[0].periodType

  if (periodType && periodType.unit === 'week' && periodType.quantity === 1) {
    return 'week'
  }

  if (periodType && periodType.unit === 'month' && periodType.quantity === 1) {
    return 'month'
  }

  if (
    periodType && periodType.unit === 'month' &&
    (periodType.quantity === 2 || periodType.quantity === 3)
  ) {
    return 'bimonth'
  }

  return null
}

class AnalyticsApi {
  constructor (pgConnection, agaveAdapter) {
    this.pgConnection = pgConnection
    this.agaveAdapter = agaveAdapter
  }

  async _resolveServiceIds (programs) {
    return resolveServiceIds(this.pgConnection, programs)
  }

  /**
   * Resolves the program options for the given programs.
   * All programs must share the same reporting cycle, otherwise returns null.
   * @param {string[]} programs - Array of program IDs
   * @returns {Promise<object|null>} The program options (with report.periods) or null
   */
  async _resolveProgramOptions (programs) {
    const normalizedPrograms = Array.isArray(programs)
      ? programs.filter(Boolean)
      : [programs].filter(Boolean)

    if (normalizedPrograms.length === 0) {
      return null
    }

    const query = buildStockReportRateProgramCyclesQuery()
    const { rows = [] } = await this.pgConnection.query(query, [normalizedPrograms])

    if (rows.length === 0) {
      return null
    }

    const cycles = rows
      .map((row) => normalizeProgramCycleFromOptions(row.options))
      .filter(Boolean)

    if (cycles.length === 0) {
      return null
    }

    // All programs must share the same cycle
    if (cycles.some((cycle) => cycle !== cycles[0])) {
      return null
    }

    // Return the first program's options (they all share the same cycle)
    return rows[0].options
  }

  async getRevenueSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getRevenueByLocationTable ({ fromDate, toDate, organisationId, location, productSku, programs, aggregationLevel }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueByLocationTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds,
      aggregationLevel
    })
  }

  async getRevenueByProductTable ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueByProductTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getRevenueMissingPricesSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getRevenueMissingPricesSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getMonthlyRevenue ({ toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getMonthlyRevenueData({
      pgConnection: this.pgConnection,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getGrossProfitSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getGrossProfitSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getGrossProfitByProductTable ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getGrossProfitByProductTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getGrossProfitByLocationTable ({ fromDate, toDate, organisationId, location, productSku, programs, aggregationLevel }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getGrossProfitByLocationTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds,
      aggregationLevel
    })
  }

  async getGrossProfitMissingPricesSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getGrossProfitMissingPricesSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getMonthlyGrossMargin ({ toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getMonthlyGrossMarginData({
      pgConnection: this.pgConnection,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getLossesSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getLossesSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getLossesByProductTable ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getLossesByProductTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getLossesByLocationTable ({ fromDate, toDate, organisationId, location, productSku, programs, aggregationLevel }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getLossesByLocationTableData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds,
      aggregationLevel
    })
  }

  async getLossesMissingPricesSummary ({ fromDate, toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getLossesMissingPricesSummaryData({
      pgConnection: this.pgConnection,
      fromDate,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getMonthlyLosses ({ toDate, organisationId, location, productSku, programs }) {
    const serviceIds = await this._resolveServiceIds(programs)
    return getMonthlyLossesData({
      pgConnection: this.pgConnection,
      toDate,
      organisationId,
      location,
      productSku,
      serviceIds
    })
  }

  async getStockReportRateSummary ({ fromDate, toDate, programs, country, zone, state, lga, sdp }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const toDateExclusiveUtc = new Date(toDateUtc)
    toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)

    // Get program options to calculate incomplete periods exclusion
    const programOptions = await this._resolveProgramOptions(programs)
    const incompletePeriodsStartDate = getIncompletePeriodsStartDate(programOptions)

    const { params, whereClauses, hasIncompletePeriodsFilter } = buildStockReportRateQueryContext({
      programs,
      fromDateUtc,
      toDateExclusiveUtc,
      incompletePeriodsStartDate,
      country,
      zone,
      state,
      lga,
      sdp
    })

    const query = buildStockReportRateSummaryQuery({
      whereClauses: whereClauses,
      hasIncompletePeriodsFilter
    })

    const { rows: [summary = {}] } = await this.pgConnection.query(query, params)
    const expectedLocations = Number(summary.expected_locations || 0)
    const completedLocations = Number(summary.completed_locations || 0)
    const total = expectedLocations > 0 ? completedLocations / expectedLocations : null

    return {
      fromDate,
      toDate,
      country: country,
      zone: zone,
      state: state,
      lga: lga,
      sdp: sdp,
      programs: params[0],
      total,
      level: getStockReportRateLevel(total),
      completedLocations,
      expectedLocations
    }
  }

  async getStockReportRateLocationTable ({ fromDate, toDate, programs, groupedBy, country, zone, state, lga, sdp }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const toDateExclusiveUtc = new Date(toDateUtc)
    toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)

    // Get program options to calculate incomplete periods exclusion
    const programOptions = await this._resolveProgramOptions(programs)
    const incompletePeriodsStartDate = getIncompletePeriodsStartDate(programOptions)

    const { params, whereClauses, hasIncompletePeriodsFilter } = buildStockReportRateQueryContext({
      programs,
      fromDateUtc,
      toDateExclusiveUtc,
      incompletePeriodsStartDate,
      country,
      zone,
      state,
      lga,
      sdp
    })

    const query = buildStockReportRateLocationTableQuery({
      whereClauses: whereClauses,
      groupedBy,
      hasIncompletePeriodsFilter
    })
    const { rows } = await this.pgConnection.query(query, params)

    const mappedRows = groupedBy === 'sdp'
      ? rows.map((row) => ({
        locationFsid: row.location_fsid,
        sdp: row.sdp,
        completed: row.completed === true,
        numberOfReportsExpected: Number(row.number_of_reports_expected || 0),
        numberOfReportsSubmitted: Number(row.number_of_reports_submitted || 0),
        expectedStockReportIds: Array.isArray(row.expected_stock_report_ids) ? row.expected_stock_report_ids : [],
        submittedStockReportIds: Array.isArray(row.submitted_stock_report_ids) ? row.submitted_stock_report_ids : []
      }))
      : rows.map((row) => {
        const buildGroupedId = () => {
          if (groupedBy === 'zone') {
            return row.zone ? `zone:${row.zone}` : (row.country ? `country:${row.country}` : null)
          }
          if (groupedBy === 'state') {
            if (row.zone) return `zone:${row.zone}:state:${row.state}`
            if (row.country) return `country:${row.country}:state:${row.state}`
            return row.state ? `state:${row.state}` : null
          }
          if (groupedBy === 'lga') {
            if (row.zone) return `zone:${row.zone}:state:${row.state}:lga:${row.lga}`
            if (row.country) return `country:${row.country}:state:${row.state}:lga:${row.lga}`
            return row.state && row.lga ? `state:${row.state}:lga:${row.lga}` : null
          }
          return null
        }
        const expectedLocations = Number(row.total_number_of_locations_expected_to_report || 0)
        const completedLocations = Number(row.number_of_locations_completed || 0)
        const value = expectedLocations > 0
          ? completedLocations / expectedLocations
          : null
        return {
          id: buildGroupedId(),
          groupedByValue: row.grouped_by_value,
          expectedLocations,
          completedLocations,
          value,
          level: getStockReportRateLevel(value)
        }
      })

    return {
      fromDate,
      toDate,
      groupedBy,
      country: country,
      zone: zone,
      state: state,
      lga: lga,
      sdp: sdp,
      programs: params[0],
      rows: mappedRows
    }
  }

  async getStockReportRateCycleByCycle ({ fromDate, toDate, programs, country, zone, state, lga, sdp }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const toDateExclusiveUtc = new Date(toDateUtc)
    toDateExclusiveUtc.setUTCDate(toDateExclusiveUtc.getUTCDate() + 1)
    // the logic below that decides the scope for which we query the data is the same as in the legacy ES solution
    // see backend/stacks/internal_api/endpoint/src/stock-report-rate.js
    const isYearOrMore = differenceInCalendarMonths(fromDateUtc, toDateUtc) >= 12
    const startDate = !isYearOrMore ? subYears(fromDateUtc, 1) : fromDateUtc
    const cycleByCycleLimit = 60
    const normalizedPrograms = Array.isArray(programs)
      ? programs.filter(Boolean)
      : [programs].filter(Boolean)

    // When multiple programs are requested, ensure they all share the same
    // reporting cycle. This should never fail for current tenants, but the
    // validation is kept for future compatibility.
    let programsForQuery = programs
    let programOptions = null
    const metadataQuery = buildStockReportRateProgramCyclesQuery()
    const { rows: metadataRows = [] } = await this.pgConnection.query(metadataQuery, [normalizedPrograms])

    if (metadataRows.length > 0) {
      const foundProgramIds = new Set(metadataRows.map((row) => row.fsid))
      const missingPrograms = normalizedPrograms.filter((id) => !foundProgramIds.has(id))
      if (missingPrograms.length > 0) {
        console.warn({
          code: 'stock-report-rate-cycle-by-cycle-programs-missing',
          requestedPrograms: normalizedPrograms,
          foundPrograms: Array.from(foundProgramIds),
          missingPrograms
        })
      }

      programsForQuery = normalizedPrograms.filter((id) => foundProgramIds.has(id))

      const cycles = metadataRows
        .map((row) => normalizeProgramCycleFromOptions(row.options))
        .filter(Boolean)

      if (cycles.length > 0) {
        if (cycles.some((cycle) => cycle == null)) {
          const err = new Error('Unable to determine the reporting cycle for one or more programs.')
          err.status = 400
          throw err
        }

        if (cycles.some((cycle) => cycle !== cycles[0])) {
          const err = new Error('Programs must share the same reporting cycle.')
          err.status = 400
          throw err
        }

        // All programs share the same cycle, use first program's options
        programOptions = metadataRows[0].options
      }
    }

    // Calculate incomplete periods start date based on program options
    const incompletePeriodsStartDate = getIncompletePeriodsStartDate(programOptions)

    // Assumption: all programs passed in programs param use the same reporting cycle.
    // We aggregate by period_start buckets exactly as stored in analytics.stock_report_rate.
    const { params, whereClauses, hasIncompletePeriodsFilter } = buildStockReportRateQueryContext({
      programs: programsForQuery,
      fromDateUtc: startDate,
      toDateExclusiveUtc,
      incompletePeriodsStartDate,
      country,
      zone,
      state,
      lga,
      sdp
    })
    const query = buildStockReportRateCycleByCycleQuery({
      whereClauses,
      limit: cycleByCycleLimit,
      hasIncompletePeriodsFilter
    })
    const { rows } = await this.pgConnection.query(query, params)
    const values = rows
      .slice()
      .sort((a, b) => String(a.period_start_date).localeCompare(String(b.period_start_date)))
      .map((row) => {
        const expectedLocations = Number(row.expected_locations || 0)
        const completedLocations = Number(row.completed_locations || 0)
        const ratio = expectedLocations > 0 ? (completedLocations / expectedLocations) : 0
        const cycleStartDate = String(row.period_start_date)
        return {
          value: parseFloat((ratio * 100).toFixed(0)),
          date: `${cycleStartDate}T00:00:00.000Z`,
          tooltip: {
            label: 'SDPs reported',
            value: completedLocations
          }
        }
      })

    return {
      items: [{
        startDate: startDate.toISOString(),
        values,
        endDate: toDateUtc.toISOString(),
        thresholds: {
          // these values are the same as in the legacy ES solution, see backend/stacks/internal_api/endpoint/src/stock-report-rate.js
          // they are kept for backwards compatibility as this has been hardcoded on the ES solution.
          max: 95,
          maxLabel: 'critical'
        },
        unit: '%'
      }]
    }
  }

  async getStockReportRateExportRows ({ fromDate, toDate, location, programs, funders, limit, offset }) {
    checkForStockReportRateRequirements(this.pgConnection, programs)
    const fromDateUtc = parseDateOnlyUtcOrThrow(fromDate, 'fromDate')
    const toDateUtc = parseDateOnlyUtcOrThrow(toDate, 'toDate')
    const { params, whereClauses, limitParam, offsetParam } = buildStockReportRateExportQueryContext({
      programs,
      fromDateUtc,
      toDateUtc,
      location,
      funders,
      limit,
      offset
    })
    const query = buildStockReportRateExportRowsQuery({ whereClauses, limitParam, offsetParam })
    const { rows = [] } = await this.pgConnection.query(query, params)
    const mappedRows = rows.map((row) => ({
      _source: {
        date: row.date,
        location: {
          id: row.location_id,
          zone: row.zone,
          state: row.state,
          lga: row.lga,
          sdp: row.sdp,
          warehouseCode: row.warehouse_code
        },
        owner: {
          program: row.program_fsid,
          service: row.service_fsid,
          funder: row.funder
        },
        value: row.submitted === true,
        submissionDate: row.submitted_at ? new Date(row.submitted_at).toISOString() : null
      }
    }))

    const resolvedLimit = Number(limit) || 5000
    const resolvedOffset = Number(offset) || 0
    return {
      rows: mappedRows,
      nextOffset: rows.length === resolvedLimit ? (resolvedOffset + resolvedLimit) : null
    }
  }

  fetchRevenueSummary (params) {
    return this.agaveAdapter.get('analytics/revenue/summary', params)
  }

  fetchRevenueByLocationTable (params) {
    return this.agaveAdapter.get('analytics/revenue/location-table', params)
  }

  fetchRevenueByProductTable (params) {
    return this.agaveAdapter.get('analytics/revenue/product-table', params)
  }

  fetchRevenueMissingPricesSummary (params) {
    return this.agaveAdapter.get('analytics/revenue/missing-prices-summary', params)
  }

  fetchMonthlyRevenue (params) {
    return this.agaveAdapter.get('analytics/revenue/monthly-revenue', params)
  }

  fetchGrossProfitSummary (params) {
    return this.agaveAdapter.get('analytics/gross-profit/summary', params)
  }

  fetchGrossProfitByProductTable (params) {
    return this.agaveAdapter.get('analytics/gross-profit/product-table', params)
  }

  fetchGrossProfitByLocationTable (params) {
    return this.agaveAdapter.get('analytics/gross-profit/location-table', params)
  }

  fetchGrossProfitMissingPricesSummary (params) {
    return this.agaveAdapter.get('analytics/gross-profit/missing-prices-summary', params)
  }

  fetchMonthlyGrossMargin (params) {
    return this.agaveAdapter.get('analytics/gross-profit/monthly-gross-margin', params)
  }

  fetchLossesSummary (params) {
    return this.agaveAdapter.get('analytics/losses/summary', params)
  }

  fetchLossesByProductTable (params) {
    return this.agaveAdapter.get('analytics/losses/product-table', params)
  }

  fetchLossesByLocationTable (params) {
    return this.agaveAdapter.get('analytics/losses/location-table', params)
  }

  fetchLossesMissingPricesSummary (params) {
    return this.agaveAdapter.get('analytics/losses/missing-prices-summary', params)
  }

  fetchMonthlyLosses (params) {
    return this.agaveAdapter.get('analytics/losses/monthly-losses', params)
  }

  fetchStockReportRateSummary (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/summary', params)
  }

  fetchStockReportRateLocationTable (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/location-table', params)
  }

  fetchStockReportRateCycleByCycle (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/cycle-by-cycle', params)
  }

  fetchStockReportRateExportRows (params) {
    return this.agaveAdapter.get('analytics/stock-report-rate/export-rows', params)
  }
}

module.exports = AnalyticsApi
